import { Target } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
	useCommitmentExceptions,
	useCommitmentMatrices,
} from '@/api/queries/commitment-matrix-queries'
import { usePeople } from '@/api/queries/people-queries'
import { CommitmentMatrixAssignmentTab } from '@/components/administration/commitment-matrix/commitment-matrix-assignment-tab'
import {
	CommitmentMatrixPeriodFilter,
	defaultPeriodFilterValue,
	type PeriodFilterValue,
} from '@/components/administration/commitment-matrix/commitment-matrix-period-filter'
import {
	ALL_SCOPE,
	CommitmentMatrixScopeFilter,
	type MatrixScope,
} from '@/components/administration/commitment-matrix/commitment-matrix-scope-filter'
import { CommitmentMatrixDashboard } from '@/components/administration/commitment-matrix/dashboard'
import { JobGroupsTab } from '@/components/administration/commitment-matrix/job-groups-tab'
import { MatricesTab } from '@/components/administration/commitment-matrix/matrices-tab'
import { PendingTab } from '@/components/administration/commitment-matrix/pending-tab'
import { AdminCardPage } from '@/components/commons'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useProfileStore } from '@/store'

export const CommitmentMatrixPage = () => {
	const navigate = useNavigate()
	const defaultBranchId = useProfileStore((s) => s.defaultBranchId)

	/* ── modo foco: filtro global em cascata Filial → CR ─────────────── */
	const [scope, setScope] = useState<MatrixScope>(ALL_SCOPE)

	/* ── filtro de período: presets + range customizado ──────────────── */
	const [period, setPeriod] = useState<PeriodFilterValue>(
		defaultPeriodFilterValue(),
	)

	/* ── busca por nome do colaborador na aba Justificativas ──────────────── */
	const [pendingPersonName, setPendingPersonName] = useState('')
	const { debouncedValue: debouncedPendingPersonName } = useDebouncedValue(
		pendingPersonName,
		500,
	)

	/* ── data ─────────────────────────────────────────────────────────── */
	const { data: matrices = [], isLoading: isLoadingMatrices } =
		useCommitmentMatrices({ limit: 100 })
	const { data: exceptionsData, isLoading: isLoadingExceptions } =
		useCommitmentExceptions({
			rangeStart: period.range.start.toISOString().slice(0, 10),
			rangeEnd: period.range.end.toISOString().slice(0, 10),
			branchId: scope.branchIds[0],
			// as justificativas só mostram colaboradores da filial selecionada no perfil
			activeBranchId: defaultBranchId || undefined,
			crId: scope.crIds[0],
			name: debouncedPendingPersonName.trim() || undefined,
		})
	const exceptions = exceptionsData?.items ?? []
	const pendingCount = exceptions.filter((e) => e.status === 'PENDENTE').length

	const { data: people } = usePeople(true)
	const personById = useMemo(
		() => new Map((people ?? []).map((p) => [p.id, p])),
		[people],
	)

	const scopeLabel = useMemo(() => {
		if (scope.crIds.length === 0 && scope.branchIds.length === 0) return null
		return scope.crIds.length > 0
			? scope.crIds.length > 1
				? 'CRs selecionados'
				: 'CR selecionado'
			: scope.branchIds.length > 1
				? 'Filiais selecionadas'
				: 'Filial selecionada'
	}, [scope])

	/* ── aba ativa: controlada p/ sincronizar com select mobile ──────── */
	const [activeTab, setActiveTab] = useState('dashboard')

	const tabOptions = [
		{ value: 'dashboard', label: 'Dashboard' },
		{ value: 'matrices', label: 'Matrizes' },
		{ value: 'job-groups', label: 'Grupos de Cargo' },
		{ value: 'assignment', label: 'Atribuição' },
		{
			value: 'pending',
			label:
				pendingCount > 0
					? `Justificativas (${pendingCount})`
					: 'Justificativas',
		},
	]

	return (
		<AdminCardPage
			icon={<Target className="size-5" />}
			title="Matriz de Comprometimento"
			subtitle="Defina metas de inspeção por pessoa, CR, grupo de cargo ou filial, e acompanhe o cumprimento da equipe."
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.COMMITMENT_MATRIX))
			}
		>
			<div className="flex flex-col gap-2">
				<CommitmentMatrixScopeFilter scope={scope} onChange={setScope} />
				{(activeTab === 'dashboard' || activeTab === 'pending') && (
					<CommitmentMatrixPeriodFilter value={period} onChange={setPeriod} />
				)}
			</div>

			{scopeLabel && (
				<p className="-mt-1 text-xs text-kaefer-medium-grey">
					Exibindo dados filtrados por <strong>{scopeLabel}</strong>.
				</p>
			)}

			<Tabs value={activeTab} onValueChange={setActiveTab}>
				<div className="sm:hidden">
					<Select value={activeTab} onValueChange={setActiveTab}>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{tabOptions.map((tab) => (
								<SelectItem key={tab.value} value={tab.value}>
									{tab.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<TabsList className="hidden w-fit sm:inline-flex">
					<TabsTrigger value="dashboard" className="flex-none">
						Dashboard
					</TabsTrigger>
					<TabsTrigger value="matrices" className="flex-none">
						Matrizes
					</TabsTrigger>
					<TabsTrigger value="job-groups" className="flex-none">
						Grupos de Cargo
					</TabsTrigger>
					<TabsTrigger value="assignment" className="flex-none">
						Atribuição
					</TabsTrigger>
					<TabsTrigger value="pending" className="flex-none">
						Justificativas
						{pendingCount > 0 && (
							<span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-kaefer-red px-1 text-[10px] font-semibold text-white">
								{pendingCount}
							</span>
						)}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="dashboard" className="pt-4">
					<CommitmentMatrixDashboard
						branchIds={scope.branchIds}
						crIds={scope.crIds}
						jobGroupClusterId={scope.jobGroupClusterId ?? undefined}
						jobGroupId={scope.jobGroupId ?? undefined}
						range={period.range}
					/>
				</TabsContent>

				<TabsContent value="matrices" className="pt-4">
					<MatricesTab matrices={matrices} isLoading={isLoadingMatrices} />
				</TabsContent>

				<TabsContent value="job-groups" className="pt-4">
					<JobGroupsTab />
				</TabsContent>

				<TabsContent value="assignment" className="pt-4">
					<CommitmentMatrixAssignmentTab
						matrices={matrices}
						branchId={scope.branchIds[0]}
						crId={scope.crIds[0]}
					/>
				</TabsContent>

				<TabsContent value="pending" className="pt-4">
					<PendingTab
						exceptions={exceptions}
						isLoading={isLoadingExceptions}
						personById={personById}
						scopeLabel={scopeLabel}
						personName={pendingPersonName}
						onPersonNameChange={setPendingPersonName}
					/>
				</TabsContent>
			</Tabs>
		</AdminCardPage>
	)
}
